import { Loader2 } from "lucide-react"
import { AnimatePresence, motion } from "motion/react"
import { memo, useCallback, useState } from "react"
import { useTweetFeedState } from "../../hooks/tweets/useTweetFeedState"
import { useTweets } from "../../hooks/tweets/useTweets"
import type { TweetSortBy } from "../../lib/types"
import { TweetCard } from "./TweetCard"
import { TweetSortToggle } from "./TweetSortToggle"
import { STAGGER_EASE } from "./tweetConstants"

interface TweetsFeedProps {
	entityKey: string
	sinceMs: number
}
const LOAD_MORE_COUNT = 5

export const TweetsFeed = memo(function TweetsFeed({ entityKey, sinceMs }: TweetsFeedProps) {
	const [sortBy, setSortBy] = useState<TweetSortBy>("recent")

	const { results, status, loadMore, isLoading, hasMore } = useTweets(
		entityKey ? { entityKey, sinceMs, sortBy, initialNumItems: 5 } : null,
	)

	const {
		contentRef,
		frozenHeight,
		captureFrozenHeight,
		handleLoadMore,
		hasLoadedOnce,
		isSwitchingSort,
		isLoadingMore,
		isWaiting,
		isEmpty,
	} = useTweetFeedState({
		results,
		status,
		isLoading,
		loadMore,
		loadMoreCount: LOAD_MORE_COUNT,
	})

	const handleSortChange = useCallback(
		(sort: TweetSortBy) => {
			captureFrozenHeight()
			setSortBy(sort)
		},
		[captureFrozenHeight],
	)

	return (
		<div>
			<div className="mb-3">
				<TweetSortToggle onSortChange={handleSortChange} sortBy={sortBy} />
			</div>

			<div ref={contentRef} style={frozenHeight ? { minHeight: frozenHeight } : undefined}>
				<AnimatePresence mode="wait">
					{isWaiting ? null : isEmpty ? (
						<motion.div
							animate={{ opacity: 1, scale: 1 }}
							className="w-full py-6 text-center"
							initial={{ opacity: 0, scale: 0.95 }}
							key="empty"
							transition={{ duration: 0.2, ease: "easeOut" }}
						>
							<p className="text-sm text-text-muted">No tweets yet</p>
						</motion.div>
					) : (
						<motion.div
							animate={{ opacity: isSwitchingSort ? 0.5 : 1 }}
							initial={hasLoadedOnce ? { opacity: 0.5 } : { opacity: 0 }}
							key="content"
							transition={{ duration: 0.25 }}
						>
							<div className="space-y-3">
								{results.map((tweet, index) => (
									<motion.div
										animate={{ opacity: 1, y: 0 }}
										initial={{ opacity: 0, y: 12 }}
										key={tweet.tweetId}
										transition={{
											duration: 0.35,
											delay: Math.min(index, 10) * 0.04,
											ease: STAGGER_EASE,
										}}
									>
										<TweetCard
											avatarUrl={tweet.avatarUrl}
											createdAtMs={tweet.createdAtMs}
											displayName={tweet.displayName}
											handle={tweet.handle}
											likeCount={tweet.likeCount}
											marketAddress={tweet.marketAddress}
											quoteCount={tweet.quoteCount}
											replyCount={tweet.replyCount}
											retweetCount={tweet.retweetCount}
											text={tweet.text}
											tweetId={tweet.tweetId}
											viewCount={tweet.viewCount}
										/>
									</motion.div>
								))}
							</div>

							{/* Load more button */}
							{hasMore && (
								<div className="flex justify-center pt-4">
									<button
										className="inline-flex items-center gap-2 rounded-xl border border-border-subtle bg-bg-card px-5 py-2.5 font-medium text-sm text-text-secondary shadow-sm transition-all hover:border-border hover:bg-bg-secondary hover:text-text-primary active:scale-[0.97] disabled:opacity-50"
										disabled={isLoadingMore}
										onClick={handleLoadMore}
										type="button"
									>
										{isLoadingMore ? (
											<>
												<Loader2 aria-hidden="true" className="size-4 animate-spin" />
												<span>Loading…</span>
											</>
										) : (
											<span>Load more tweets</span>
										)}
									</button>
								</div>
							)}
						</motion.div>
					)}
				</AnimatePresence>
			</div>
		</div>
	)
})
